import { useFragment } from '@apollo/client/react';
import { BTC_GAME_FRAGMENT } from '@optools/services/operator-gateway/btc/fragments';
import { CopyrightStream } from '@optools/tools/btc-tools/components/copyright-streams/copyright-stream';
import { BroadcastSourceControls } from '@optools/tools/btc-tools/components/pipeline-detail/broadcast-source-controls';
import { EVIControls } from '@optools/tools/btc-tools/components/pipeline-detail/evi/evi-controls';
import { MultiRegionBroadcastSourceControls } from '@optools/tools/btc-tools/components/pipeline-detail/multi-region-broadcast-source-controls';
import { PipelineDetailHeader } from '@optools/tools/btc-tools/components/pipeline-detail/pipeline-detail-header';
import { RegionalPipelineControls } from '@optools/tools/btc-tools/components/pipeline-detail/regional-pipeline-controls';
import { PipelineMedia } from '@optools/tools/btc-tools/components/pipeline-media';
import {
  pipelineParamsSchema,
  pipelineSearchSchema,
} from '@optools/tools/btc-tools/schemas/pipeline';
import { getMediaId, getTeamByFeedType, hasVideoMedia } from '@optools/utils/pipeline';
import { createFileRoute, stripSearchParams, useParams, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';

/* eslint-disable perfectionist/sort-objects */
export const Route = createFileRoute('/_auth/btc-tools/schedule/$gamePk/pipelines/$feedType')({
  component: RouteComponent,
  params: { parse: zodValidator(pipelineParamsSchema).parse },
  validateSearch: zodValidator(pipelineSearchSchema),
  search: { middlewares: [stripSearchParams({ asset: '', mediaId: '' })] },
});
/* eslint-enable perfectionist/sort-objects */

export function RouteComponent() {
  const { date } = useSearch({ from: '/_auth/btc-tools/schedule' });
  const { feedType, gamePk } = useParams({
    from: '/_auth/btc-tools/schedule/$gamePk/pipelines/$feedType',
  });

  const gameFragment = useFragment({
    fragment: BTC_GAME_FRAGMENT,
    fragmentName: 'BTCGameFields',
    from: { __typename: 'Game', gamePk },
  });

  if (!gameFragment.complete) return null;

  const { data: game } = gameFragment;
  const { away, gameDate, home } = game;

  const pipeline = game.pipelines?.find((p) => p.feedType === feedType);

  if (!pipeline) {
    return (
      <div className="flex justify-center">
        <h1 className="text-lg font-bold">Could not find pipeline with feed type: {feedType}</h1>
      </div>
    );
  }

  const { eviPod, eviStatus, status } = pipeline;

  const mediaId = getMediaId(pipeline);
  const isVideoPipeline = hasVideoMedia(pipeline);
  const team = getTeamByFeedType({ away, feedType, home });
  const regionalPipelines = pipeline.regionalPipelines;

  return (
    <section className="flex flex-col gap-16">
      <div className="flex flex-col gap-2">
        <PipelineDetailHeader
          date={date}
          gamePk={gamePk}
          mediaId={mediaId}
          pipeline={pipeline}
          team={team}
        />
        <div className="flex h-full flex-wrap gap-2">
          {isVideoPipeline ? (
            regionalPipelines.length > 0 ? (
              <MultiRegionBroadcastSourceControls
                date={date}
                feedType={feedType}
                gamePk={gamePk}
                mediaId={mediaId}
                pipeline={pipeline}
              />
            ) : (
              <BroadcastSourceControls
                date={date}
                feedType={feedType}
                gamePk={gamePk}
                mediaId={mediaId}
                pipeline={pipeline}
              />
            )
          ) : null}
          <div className="flex flex-col justify-between gap-2">
            {regionalPipelines.length > 0 && (
              <RegionalPipelineControls
                gamePk={gamePk}
                mediaId={mediaId}
                regionalPipelines={regionalPipelines}
              />
            )}
            <EVIControls
              eviPod={eviPod}
              eviStatus={eviStatus}
              mediaId={mediaId}
              pipelineStatus={status}
            />
          </div>
        </div>
      </div>

      <CopyrightStream
        awayTriCode={away.abbreviation}
        feedType={feedType}
        gamePk={gamePk}
        gameStartTime={gameDate}
        homeTriCode={home.abbreviation}
        scheduleDate={date}
      />

      <PipelineMedia gamePk={gamePk} pipeline={pipeline} />
    </section>
  );
}
